import { zodResolver } from '@hookform/resolvers/zod';
import { getParty } from 'graphql/parties/queries';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import graphql from 'services/querys';
import useAlert from 'utils/alert';
import { NewUserType } from 'v2-types/user';
import { getContactErrorMessage } from 'views/contacts/services/errors';

import { useCreateUser } from '../hooks/useCreateUser';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { addLoginEmailIfNotExist } from './services';
import useMutations from './services/useMutations';
import schema from './services/validations';

const useContactEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const showAlert = useAlert();
  const memorizedSchema = useMemo(() => schema(t), [t]);
  const methods = useForm<NewUserType>({
    resolver: zodResolver(memorizedSchema),
    mode: 'onChange',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [modalInvite, setModalInvite] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<{
    state: boolean;
    kind: 'delete' | 'unlink';
  }>({
    state: false,
    kind: 'delete',
  });
  const [modalCreateMemberOpen, setModalCreateMemberOpen] = useState<boolean>(false);

  const getPartyResponse = useQuery(['getContactEditData', id], {
    queryFn: () => getParty({ id }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.getParty) {
        methods.reset(data.getParty);
      }
    },
  });

  const mutationResult = useMutation(graphql.deleteParty);

  const deleteParty = mutationResult.mutate;

  useMutations(history, methods, setErrors, mutationResult, t);

  const { mutate: createUserMutation, ...responseCreateUserMutation } = useCreateUser({
    onSuccess(data) {
      responseCreateUserMutation.reset();
      methods.setValue('user_id', data.createUser._id);
      methods.setValue('username', data.createUser.username);
      const emails = addLoginEmailIfNotExist(
        data.createUser.emails,
        data.createUser.username,
        data.createUser._id
      );
      methods.setValue('emails', emails);
      showAlert(t('users:user-linked-success'), 'success');
    },
    onError(error) {
      responseCreateUserMutation.reset();
      setErrors((prev) => [...prev, getContactErrorMessage(error, t)]);
    },
  });

  const { mutate: deleteUser, ...responseDeleteUser } = useDeleteUser({
    onSuccess: (data) => {
      responseDeleteUser.reset();
      methods.setValue('user_id', data.deleteUser.user_id);
      methods.setValue('username', data.deleteUser.username);
      methods.setValue('emails', data.deleteUser.emails);
      showAlert(t('users:user-unlinked-success'), 'success');
    },
    onError: () => {
      responseDeleteUser.reset();
    },
  });

  return {
    t,
    _id: id,
    methods,
    history,
    errors,
    loading: getPartyResponse.isLoading,
    modalInvite,
    setModalInvite,
    deleteUser,
    deleteParty,
    isDeletingParty: mutationResult.isLoading,
    confirmModalOpen,
    setConfirmModalOpen,
    createUserMutation,
    setErrors,
    modalCreateMemberOpen,
    setModalCreateMemberOpen,
    getPartyResponse,
  };
};

export default useContactEdit;
